import * as v from 'valibot';

import { EResourceStatus, EResourceType } from '#shared';

import { ENOPOrganTypeSchema } from './organ.schema';
import { trialProgramSchema } from './trialProgram.schema';

export enum EScheduleType {
  OnCall = 'OnCall',
  Scheduled = 'Scheduled',
}

export const EResourceStatusSchema = v.enum(
  EResourceStatus,
  'EResourceStatusSchema',
);

export const EResourceTypeSchema = v.enum(EResourceType, 'EResourceTypeSchema');

export const EScheduleTypeSchema = v.enum(EScheduleType, 'EScheduleTypeSchema');

/**
 * Hub location information
 */
export const hubLocationSchema = v.object({
  /** Hub unique identifier */
  id: v.string('HubLocationSchema.id'),
  /** Hub display name */
  name: v.string('HubLocationSchema.name'),
  /** Hub code/abbreviation */
  abbreviation: v.string('HubLocationSchema.abbreviation'),
  /** Geographic region */
  region: v.string('HubLocationSchema.region'),
  /** US state */
  state: v.string('HubLocationSchema.state'),
  /** City name */
  city: v.string('HubLocationSchema.city'),
  /** IANA timezone identifier (e.g., 'America/New_York') */
  timezone: v.string('HubLocationSchema.timezone'),
  /** Geographic coordinates */
  lonLat: v.tuple(
    [
      v.number('HubLocationSchema.lonLat.0'),
      v.number('HubLocationSchema.lonLat.1'),
    ],
    'HubLocationSchema.lonLat',
  ),
});

export type HubLocation = v.InferInput<typeof hubLocationSchema>;

/**
 * Schedule segment for a resource
 */
export const resourceScheduleSchema = v.object({
  /** ISO 8601 start datetime (UTC) */
  startUTCDateTime: v.string('resourceScheduleSchema.startUTCDateTime'),
  /** Start date in hub local timezone (MM/DD/YYYY) */
  startDate: v.string('resourceScheduleSchema.startDate'),
  /** Start time in hub local timezone (h:mm a) */
  startTime: v.string('resourceScheduleSchema.startTime'),
  /** ISO 8601 end datetime (UTC) */
  endUTCDateTime: v.string('resourceScheduleSchema.endUTCDateTime'),
  /** End date in hub local timezone (MM/DD/YYYY) */
  endDate: v.string('resourceScheduleSchema.endDate'),
  /** End time in hub local timezone (h:mm a) */
  endTime: v.string('resourceScheduleSchema.endTime'),
  /** Schedule type */
  scheduleType: EScheduleTypeSchema,
  /** Current status */
  status: EResourceStatusSchema,
  /** Hub where this shift takes place */
  hubLocation: hubLocationSchema,
});

export type ResourceSchedule = v.InferInput<typeof resourceScheduleSchema>;
/**
 * Base resource entry
 */
const baseResourceSchema = v.object({
  /** Resource display name */
  name: v.string('baseResourceSchema.name'),
  /** Current availability status */
  status: EResourceStatusSchema,
  /** UNOS donor ID if assigned to a case */
  unosId: v.nullish(v.string('baseResourceSchema.unosId')),
});

/**
 * Human resource (Surgeon/Specialist) entry
 */
export const humanResourceSchema = v.object({
  ...baseResourceSchema.entries,
  /** Today's schedule segment (if applicable) */
  today: v.nullish(resourceScheduleSchema),
  // /** Tomorrow's schedule segment (if applicable) */
  tomorrow: v.nullish(resourceScheduleSchema),
  /** Trial program if specialist is part of one */
  trialPrograms: v.array(trialProgramSchema),
});

export type HumanResource = v.InferInput<typeof humanResourceSchema>;

/**
 * Device resource entry
 */
export const deviceResourceSchema = v.object({
  ...baseResourceSchema.entries,
  /** Device serial number */
  serialNumber: v.string('deviceResourceSchema.serialNumber'),
  /** Trial program if device is part of one */
  trialProgram: v.nullable(trialProgramSchema),
});

export type DeviceResource = v.InferInput<typeof deviceResourceSchema>;

/**
 * Resource entry (union type)
 */
export const resourceSchema = v.union(
  [humanResourceSchema, deviceResourceSchema],
  'ResourceSchema',
);

export type Resource = v.InferInput<typeof resourceSchema>;
/**
 * Organ-specific detail within a staff category
 */
export const organDetailSchema = v.object({
  /** Unique identifier for this organ detail */
  id: v.string('OrganDetailSchema.id'),
  /** Organ type */
  organType: ENOPOrganTypeSchema,
  /** List of resources for this organ */
  resources: v.array(resourceSchema),
});

export type OrganDetail = v.InferInput<typeof organDetailSchema>;

const staffCountsInfoSchema = v.object({
  /** Unique identifier for this staff counts info */
  id: v.string('StaffCountsInfo.id'),
  /** Organ type */
  organType: ENOPOrganTypeSchema,
  /** Available count */
  availableCount: v.number('StaffCountsInfo.availableCount'),
  /** Assigned count */
  assignedCount: v.number('StaffCountsInfo.assignedCount'),
  /** Available trial count */
  availableTrialCount: v.number('StaffCountsInfo.availableTrialCount'),
  /** Assigned trial count */
  assignedTrialCount: v.number('StaffCountsInfo.assignedTrialCount'),
});

export type StaffCountsInfo = v.InferInput<typeof staffCountsInfoSchema>;
/**
 * Staff category information
 */
export const staffInfoSchema = v.object({
  /** Unique identifier for this staff category */
  id: v.string('Staff info ID is missing'),
  /** Type of team member */
  teamMemberType: EResourceTypeSchema,
  /** Details per organ type */
  details: v.array(organDetailSchema, 'Staff details are missing'),
  /** Optional counts information */
  countsInfo: v.nullish(v.array(staffCountsInfoSchema, 'Invalid counts info')),
});

export type StaffInfo = v.InferInput<typeof staffInfoSchema>;

/**
 * Hub resource report status
 *
 * Main response object for the hubs resource report endpoint
 */
export const hubReportStatusSchema = v.object({
  /** Hub code/ID */
  id: v.string('HubReportStatusSchema.id'),
  /** Hub location information */
  location: hubLocationSchema,
  /** Staff information grouped by type */
  staffInfo: v.array(staffInfoSchema, 'HubReportStatusSchema.staffInfo'),
});

export type HubReportStatus = v.InferInput<typeof hubReportStatusSchema>;
